import { InputField } from '@/components/form/InputField';
import ToggleableRow from '@/components/purchase/checkout/ToggleableRow';
import type { BookingPassenger } from '@/types/passenger';
import type { SendToEmailFormValues } from '@/types/sendTicketsToEmail';
import { Button, Checkbox, Icons, Typography } from '@fleet/webshop-ui';
import { type FC, useCallback, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

interface PassengerSelectionCardProps {
  bookingPassenger: BookingPassenger;
}

const PassengerSelectionCard: FC<PassengerSelectionCardProps> = ({
  bookingPassenger,
}) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<SendToEmailFormValues>();
  const [isEditing, setIsEditing] = useState(false);
  const [originalEmail, setOriginalEmail] = useState('');

  const formPassengers =
    useWatch({
      control,
      name: 'passengers',
    }) || [];

  const passengerId = `${bookingPassenger.id}`;
  const formPassengerIndex = formPassengers.findIndex(
    (p) => p.passengerId === passengerId
  );
  const isSelected = formPassengerIndex >= 0;
  const registeredEmail =
    bookingPassenger.contactInformation?.emailAddress || null;
  const firstName = bookingPassenger.firstName || '';
  const lastName = bookingPassenger.lastName || '';

  const currentFormEmail = isSelected
    ? formPassengers[formPassengerIndex].emailOverride?.[0] || ''
    : '';

  const hasFieldError =
    isSelected && errors.passengers?.[formPassengerIndex]?.emailOverride;

  const handleToggle = useCallback(() => {
    const existingIndex = formPassengers.findIndex(
      (p) => p.passengerId === passengerId
    );

    if (existingIndex >= 0) {
      // Remove passenger from array
      setValue(
        'passengers',
        formPassengers.filter((_, i) => i !== existingIndex)
      );
    } else {
      // Add passenger to array
      setValue('passengers', [
        ...formPassengers,
        {
          passengerId,
          sendSms: false,
          sendEmail: true,
          emailOverride: [registeredEmail || ''],
        },
      ]);
    }
  }, [formPassengers, setValue, passengerId, registeredEmail]);

  const handleEdit = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // Store original value for potential rollback
      setOriginalEmail(currentFormEmail);
      setIsEditing(true);
    },
    [currentFormEmail]
  );

  const handleSubmit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    // Form already has the changes, just exit edit mode
    setIsEditing(false);
  }, []);

  const handleDiscard = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // Restore original value, trigger validation to clear errors, and exit edit mode
      setValue(
        `passengers.${formPassengerIndex}.emailOverride.0`,
        originalEmail,
        {
          shouldValidate: true,
        }
      );
      setIsEditing(false);
    },
    [formPassengerIndex, originalEmail, setValue]
  );

  return (
    <ToggleableRow
      isActive={isSelected}
      onClick={!isEditing ? handleToggle : undefined}
      className="flex flex-row gap-3"
    >
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox
          isSelected={isSelected}
          isDisabled={isEditing}
          onChange={() => handleToggle()}
        />
      </div>
      <div className="flex flex-1 flex-col">
        <Typography variant="body1-bold">
          {firstName} {lastName}
        </Typography>
        {!isEditing ? (
          <Typography variant="body2" className="text-neutral">
            {isSelected ? currentFormEmail || '-' : registeredEmail || '-'}
          </Typography>
        ) : (
          <InputField
            name={`passengers.${formPassengerIndex}.emailOverride.0`}
            label="email"
            hideLabel
            endAdornment={
              currentFormEmail ? (
                <Button
                  variant="ghost"
                  onClick={() =>
                    setValue(
                      `passengers.${formPassengerIndex}.emailOverride.0`,
                      '',
                      { shouldValidate: true }
                    )
                  }
                  data-testid={`clear-passenger-email-${passengerId}`}
                  aria-label="Clear email"
                  className="text-dark"
                >
                  <Icons.xSmall />
                </Button>
              ) : null
            }
          />
        )}
      </div>
      {!isEditing ? (
        <Button
          variant="secondary"
          size="small"
          className="aspect-square w-fit rounded-full p-2"
          type="button"
          onClick={handleEdit}
          isDisabled={!isSelected}
          data-testid={`edit-passenger-email-${passengerId}`}
        >
          <Icons.edit width={16} height={16} />
        </Button>
      ) : (
        <div className="flex flex-col gap-2">
          <Button
            variant="secondary"
            size="small"
            className="aspect-square h-8 w-8 rounded-full p-2 text-success-text"
            type="button"
            isDisabled={!currentFormEmail || !!hasFieldError}
            onClick={handleSubmit}
            data-testid={`submit-passenger-email-${passengerId}`}
          >
            <Icons.check width={16} height={16} />
          </Button>
          <Button
            variant="secondary"
            size="small"
            className="aspect-square h-8 w-8 rounded-full p-2 text-error-text"
            type="button"
            onClick={handleDiscard}
            data-testid={`discard-passenger-email-${passengerId}`}
          >
            <Icons.xSmall width={16} height={16} />
          </Button>
        </div>
      )}
    </ToggleableRow>
  );
};

export default PassengerSelectionCard;
